import styled from '@emotion/styled';
import { Divider } from '@material-ui/core';
import Button from 'aquilla/core/molecules/Button';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getMe } from '@api/selectors/auth';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useToggleable } from '@stores/toggleables';
import { segment } from '@actions/analytics';
import { mediaQuery } from '@styles/index';
import { useMemo } from 'react';
import {
  CustomSKUCard,
  HybridSKUCard,
  useTestSeriesData
} from './useTestSeriesData';
import { POSSIBLE_F1, POSSIBLE_F1_UID, UAITS_EVENT_MAP } from './constants';

const StyledTestSeriesCard = styled.div`
  box-shadow: 0px 12px 24px 0px var(--color-shadow);
  background-color: var(--color-base-1);
  border-radius: 8px;
  display: flex;
  width: 70%;
  height: 100%;
  padding: 8px;

  ${mediaQuery.uptoMobile} {
    width: 100%;
    flex-direction: column;
  }
`;

const ImageWrapper = styled.img`
  background-color: var(--color-base-0);
  border-radius: 8px;
  align-self: center;
  width: 40%;
  height: 100%;
  object-fit: cover;

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const TestSeriesCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 60%;
  padding: 24px;

  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: 16px;
  }
`;

const Title = styled.p`
  font-weight: 700;
  font-size: 32px;
  line-height: 40px;
  letter-spacing: -1%;
  color: var(--color-text-primary);

  ${mediaQuery.uptoMobile} {
    font-size: 24px;
    line-height: 32px;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledFilterChip = styled.div`
  height: 24px;
  padding-top: 2px;
  padding-right: 12px;
  padding-bottom: 2px;
  padding-left: 12px;
  gap: 12px;
  border-radius: 35px;
  background-color: var(--color-base-0);
  color: var(--color-primary-focus);
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
`;

const PointsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Point = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const PointText = styled.p``;

const PriceSection = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;

  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;

  ${mediaQuery.uptoMobile} {
    width: 100%;
    flex-direction: column;
    gap: 16px;
  }
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DiscountedPrice = styled.p`
  font-weight: 600;
  font-size: 22px;
  line-height: 24px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: var(--color-text-primary);
`;

const OriginalPrice = styled.p`
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: var(--color-text-secondary);
`;

const Tag = styled.div`
  display: inline;
  background-color: var(--color-green-bg);
  color: var(--color-green-focus);
  font-weight: 700;
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 4px;
  line-height: 120%;
  letter-spacing: 0%;
  text-align: center;
  text-transform: uppercase;
`;

const PointIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const TestSeriesCard = ({
  cardData
}: {
  cardData: CustomSKUCard | HybridSKUCard;
}) => {
  const meData = useMyInfo(getMe);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const { setShowCenterSelectionModal } = useTestSeriesData();

  const {
    f1,
    f2,
    recommended,
    strikePrice,
    skuTag,
    title,
    skuOriginalPrice,
    discountPercent,
    keyFeature,
    schedulePdf,
    gridImage
  } = cardData;

  const points = useMemo(
    () =>
      keyFeature?.map((point) => ({
        text: point.keyFeature,
        iconUrl: point.keyFeatureIcon?.url
      })) || [],
    [keyFeature]
  );
  return (
    <StyledTestSeriesCard>
      <ImageWrapper src={gridImage?.url} />
      <TestSeriesCardContent>
        <TagsContainer>
          {skuTag?.map((tag) => (
            <StyledFilterChip key={tag.id}>{tag.skuTag}</StyledFilterChip>
          ))}
        </TagsContainer>
        <Title>{title}</Title>
        <PointsContainer>
          {points?.map((point) => (
            <Point key={point.text}>
              <PointIcon src={point.iconUrl} />
              <PointText>{point.text}</PointText>
            </Point>
          ))}
        </PointsContainer>
        <Divider style={{ margin: '16px 0' }} />
        <PriceSection>
          <PriceContainer>
            <OriginalPrice>
              <span style={{ textDecoration: 'line-through' }}>
                ₹{strikePrice}
              </span>{' '}
              ({discountPercent}% off)
            </OriginalPrice>
            <DiscountedPrice>
              ₹{skuOriginalPrice} only{' '}
              {recommended && (
                <Tag>
                  <span>RECOMMENDED</span>
                </Tag>
              )}
            </DiscountedPrice>
          </PriceContainer>
          <ButtonsContainer>
            <Button
              variant="secondary"
              size="medium"
              label="View Schedule"
              onClick={() => {
                segment.track(UAITS_EVENT_MAP.CLICKED_ON_VIEW_SCHEDULE, {
                  userId: meData?.uid,
                  current_page_url: window.location.href,
                  login_status: isLoggedIn,
                  page_title: 'UAITS',
                  goal_name: f1,
                  goal_uid: POSSIBLE_F1_UID[POSSIBLE_F1.indexOf(f1)],
                  learner_class: f2,
                  test_name: title
                });
                window.open(schedulePdf?.url, '_blank');
              }}
            />
            <Button
              variant="primary"
              size="medium"
              label="Buy Test Series"
              onClick={() => {
                if (!isLoggedIn) {
                  openLoginDrawer();
                  return;
                }

                segment.track(UAITS_EVENT_MAP.CHECKOUT_STARTED, {
                  userId: meData?.uid,
                  current_page_url: window.location.href,
                  login_status: isLoggedIn,
                  page_title: 'UAITS',
                  goal_name: f1,
                  goal_uid: POSSIBLE_F1_UID[POSSIBLE_F1.indexOf(f1)],
                  learner_class: f2,
                  test_name: title
                });
                if ('skuLink' in cardData) {
                  window.open(cardData.skuLink, '_blank');
                } else {
                  setShowCenterSelectionModal({
                    show: true,
                    data: cardData
                  });
                }
              }}
            />
          </ButtonsContainer>
        </PriceSection>
      </TestSeriesCardContent>
    </StyledTestSeriesCard>
  );
};

export default TestSeriesCard;
